import React from 'react';
import CareMapprButton from '../../components/Button/CareMapprButton';
import { ExclamationCircle, Search, X } from 'react-bootstrap-icons';
import { Accordion, Card, useAccordionButton } from 'react-bootstrap';

import './style.css';
import SavedFilterItem from '../searchPage/components/savedFilters/SavedFilterItem';
import { useDispatch, useSelector, batch } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import {
  setCurrentFilter,
  setFromNavBar,
  setUserSavedFilters,
} from '../../../store/ai2';
import type {
  SavedSearchFilter,
  SaveSearchFilterPayload,
} from '../searchPage/types';
import { post } from '../../../api/baseUrl';
import {
  SystemPermission,
  useEnvironment,
  useToast,
  useTriggerUpdate,
} from '../../../context';
import {
  convertToFilterString,
  sanitizeSearchFilterTokens,
} from '../searchPage/utils';

interface AccordionWrapperProps {
  children: React.ReactNode;
  eventKey: string;
  className?: string;
}
const AccordionWrapper = ({
  children,
  eventKey,
  className = '',
}: AccordionWrapperProps) => {
  const onClick = useAccordionButton(eventKey, () => {});
  return (
    <button className={className} onClick={onClick}>
      {children}
    </button>
  );
};

const DashboardSavedSearchFilters = () => {
  const { setToast } = useToast();
  const { env } = useEnvironment();
  const { resetHaveMoreData } = useTriggerUpdate();

  const [isSaving, setIsSaving] = React.useState(false);
  const dispatch: AppDispatch = useDispatch();
  const { userSavedFilters } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );
  const { fromNavBar } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const searchFiltersMap = selectedTenancy?.features?.searchFiltersMap || {};
  const canAccessOpenSafetyAlerts =
    !selectedTenancy?.permissionsSetup ||
    !!selectedTenancy?.userPermissions?.[SystemPermission.SAFETY_MONITORING];
  const canAccessReadyForReview =
    !selectedTenancy?.permissionsSetup ||
    !selectedTenancy?.userPermissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const visibleUserSavedFilters = React.useMemo(() => {
    return userSavedFilters.filter((item) => {
      const filters = convertToFilterString(item, searchFiltersMap);
      const sanitizedFilters = sanitizeSearchFilterTokens(
        filters,
        searchFiltersMap,
        {
          allowOpenSafetyAlerts: canAccessOpenSafetyAlerts,
          allowReadyForReview: canAccessReadyForReview,
        },
      );

      return (
        sanitizedFilters.length === filters.length &&
        sanitizedFilters.every((filter, index) => filter === filters[index])
      );
    });
  }, [
    canAccessOpenSafetyAlerts,
    canAccessReadyForReview,
    searchFiltersMap,
    userSavedFilters,
  ]);

  const fullSavedFilters = React.useMemo(() => {
    if (env === 'development') {
      return [
        {
          id: 'all-patient-filter',
          text: 'All Patients',
          pinned: false,
        },
        ...visibleUserSavedFilters,
      ];
    }

    return [...visibleUserSavedFilters];
  }, [env, visibleUserSavedFilters]);

  const checkSearchFilterDuplicate = (
    filterText: string,
    userSavedFilters: SavedSearchFilter[],
  ): boolean => {
    const normalize = (text: string) =>
      text
        .split(/\s*,\s*/)
        .map((str) => str.trim().toLowerCase())
        .filter((str) => str.length > 0)
        .sort()
        .join(',');

    const normalizedSearchText = normalize(filterText);

    return userSavedFilters.some(
      (filter) => normalize(filter.text) === normalizedSearchText,
    );
  };

  const pinedFilter =
    visibleUserSavedFilters.find((item) => item.pinned) ||
    visibleUserSavedFilters[0];
  const { currentFilters } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );

  const savedSearchDisabled =
    selectedTenancy?.features?.searchScreen?.savedSearchDisabled ?? false;

  const filterText = React.useMemo(() => {
    if (savedSearchDisabled) {
      return currentFilters?.length > 0
        ? currentFilters.join(', ')
        : 'All Patients';
    }
    if (fromNavBar) {
      return pinedFilter?.text || 'All Patients';
    }
    if (currentFilters?.length === 0) {
      if (visibleUserSavedFilters.length > 0) {
        return visibleUserSavedFilters[0]?.text || 'All Patients';
      }
      return 'All Patients';
    }
    return currentFilters?.length > 0
      ? currentFilters.join(', ')
      : pinedFilter?.text || visibleUserSavedFilters[0]?.text || 'All Patients';
  }, [
    currentFilters,
    fromNavBar,
    pinedFilter?.text,
    savedSearchDisabled,
    visibleUserSavedFilters,
  ]);

  const onClickFilter = (id: string) => {
    if (savedSearchDisabled) return;
    resetHaveMoreData(true);
    if (id === 'all-patient-filter') {
      batch(() => {
        dispatch(setFromNavBar(false));
        dispatch(setCurrentFilter(['All Patients']));
      });
      return;
    }
    const found = visibleUserSavedFilters.find((item) => item.id === id);
    const filters = convertToFilterString(found, searchFiltersMap);
    batch(() => {
      dispatch(setFromNavBar(false));
      dispatch(setCurrentFilter(filters));
    });
  };

  const isDuplicate = checkSearchFilterDuplicate(
    filterText,
    visibleUserSavedFilters,
  );

  const onSaveSearchToPatient = async () => {
    if (savedSearchDisabled) return;
    if (!selectedTenancy) return;

    if (isDuplicate) {
      setToast(
        <div className="d-flex justify-content-center align-items-center gap-3 text-center">
          This filter already exists <ExclamationCircle color="#E0B36A" />
        </div>,
      );
      return;
    }
    try {
      setIsSaving(true);
      const payload: SaveSearchFilterPayload = {
        filterText,
        tenancyId: selectedTenancy.tenancy_id,
      };
      const {
        data: { data },
      } = await post('/saveSearchFilter', payload);
      dispatch(setUserSavedFilters([...userSavedFilters, data]));
      return data;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Accordion
      className="dashboard-layout"
      style={{ paddingTop: '16px', paddingBottom: '16px' }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex gap-1 align-items-center">
          <span className="roboto font-weight-400">{`Showing results for: { ${filterText} }`}</span>
          {!savedSearchDisabled &&
            !isDuplicate &&
            filterText !== 'All Patients' && (
              <CareMapprButton
                onClick={onSaveSearchToPatient}
                label={isSaving ? 'Saving ...' : 'Save this search'}
                variant="link"
              />
            )}
        </div>
        {!savedSearchDisabled && (
          <AccordionWrapper eventKey="0">
            <CareMapprButton
              onClick={() => {}}
              label="Saved search filters"
              variant="secondary"
              startIcon={<Search color="#654B78" />}
            />
          </AccordionWrapper>
        )}
      </div>
      {!savedSearchDisabled && (
        <Accordion.Collapse eventKey="0">
          <Card.Body className="saved-search-filter-container">
            <div className="d-flex flex-wrap align-items-center gap-1 me-auto">
              {fullSavedFilters.map((item) => {
                return (
                  <SavedFilterItem
                    id={item.id}
                    key={item.id}
                    text={item.text}
                    pinned={item.pinned}
                    showPinAndTrash={false}
                    onClick={onClickFilter}
                  />
                );
              })}
            </div>
            <AccordionWrapper eventKey="0" className="d-flex">
              <X size={24} />
            </AccordionWrapper>
          </Card.Body>
        </Accordion.Collapse>
      )}
    </Accordion>
  );
};

export default DashboardSavedSearchFilters;
